import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { OpportunityProduct } from '../../../../../types/opportunity';
import { v4 as uuid } from 'uuid';

export enum FormComponentChange {
  Added = 'added',
  Updated = 'updated',
  Deleted = 'deleted',
  NoChange = 'no-change'
}

export enum FormComponentType {
  Outcome = 'outcome',
  UseCase = 'usecase',
  TestCase = 'testcase'
}

export enum FormErrorType {
  Form = 'form',
  Api = 'api'
}

interface FormMap {
  [formId: string]: FormMapEntry;
}

interface FormMapEntry {
  formSectionId: string;
  type: FormComponentType;
  children: string[];
  parent: string | undefined;
  change: FormComponentChange;
  data: FormMapEntryData;
  error: string;
  errorType?: FormErrorType;
}

export interface FormMapEntryData {
  id?: number;
  title?: string;
  description?: string;
  product_use_case?: number | null;
  product_feature_success_criteria?: number | null;
  is_custom?: boolean;
  [key: string]: any;
}

interface TestCaseFormState {
  formDataMap: FormMap;
  opportunityProduct: OpportunityProduct | null;
}

const initialState: TestCaseFormState = {
  formDataMap: {},
  opportunityProduct: null,
};

interface TestCaseFormContextType {
  opportunityProduct: OpportunityProduct | null;
  setForm: (items: (FormMapEntryData & { type: FormComponentType; parent?: string; children?: string[] })[]) => void;
  setFormError: (formId: string, value: string, type?: FormErrorType) => void;
  resetForm: () => void;
  getFormIdsForType: (type: FormComponentType) => string[];
  setFormItem: (formId: string, value: any) => void;
  getFormItem: (formId: string) => FormMapEntry | undefined;
  addFormItem: (type: FormComponentType, parent?: string | undefined, toFront?: boolean) => string;
  addFormItemWithData: (
    type: FormComponentType,
    data: FormMapEntryData,
    parent?: string | undefined,
    toFront?: boolean
  ) => string;
  updateFormItem: (formId: string, field: string, value: any) => void;
  removeFormItem: (formId: string | string[]) => void;
  setFormItemSaved: (formId: string, data: Record<string, any>) => void;
  replaceChildren: (parentId: string, childIds: string[]) => void;
}

const TestCaseFormContext = createContext<TestCaseFormContextType | undefined>(undefined);

export function TestCaseFormProvider({
                                       children,
                                       opportunityProduct,
                                     }: {
  children: React.ReactNode;
  opportunityProduct: OpportunityProduct;
}) {
  const [form, setFormData] = useState<TestCaseFormState>({ ...initialState, opportunityProduct });

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      opportunityProduct,
    }));
  }, [opportunityProduct]);

  const setForm = useCallback((items: (FormMapEntryData & { type: FormComponentType; parent?: string; children?: string[] })[]) => {
    setFormData((prev) => ({
      ...prev,
      formDataMap: {
        ...items.reduce((acc: FormMap, { id, type, children = [], parent = undefined, ...data }) => {
          const uniqueId = `${type}-${id ?? uuid()}`;
          acc[uniqueId] = {
            formSectionId: uniqueId,
            type,
            change: prev.formDataMap[uniqueId]?.change ?? FormComponentChange.NoChange,
            data: { ...data, id },
            children,
            parent,
            error: undefined,
            errorType: undefined,
          };
          return acc;
        }, {} as FormMap),
      },
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      formDataMap: {},
    }));
  }, []);

  const getFormIdsForType = useCallback((type: FormComponentType) => {
    return Object.values(form.formDataMap)
      .filter((item) => item.type === type)
      .map((item) => item.formSectionId);
  }, [form.formDataMap]);

  const getFormItem = useCallback((formId: string) => {
    return form.formDataMap[formId];
  }, [form.formDataMap]);

  const setFormItem = useCallback((formId: string, data: any) => {
    setFormData((prev) => ({
      ...prev,
      formDataMap: {
        ...prev.formDataMap,
        [formId]: {
          ...prev.formDataMap[formId],
          data,
        },
      },
    }));
  }, []);

  const setItemChild = useCallback((parentId: string, childId: string, toFront = false) => {
    setFormData((prev) => ({
      ...prev,
      formDataMap: {
        ...prev.formDataMap,
        [parentId]: {
          ...prev.formDataMap[parentId],
          children: toFront
            ? [childId, ...(prev.formDataMap[parentId]?.children ?? [])]
            : [...(prev.formDataMap[parentId]?.children ?? []), childId],
        },
      },
    }));
  }, []);

  const addFormItem = useCallback((type: FormComponentType, parent: string | undefined = undefined, toFront = false) => {
    const uniqueId = `${type}-${uuid()}`;

    const defaultData =
      type === FormComponentType.UseCase
        ? {
          is_custom: false,
          product_use_case: null,
          title: '',
          description: '',
        }
        : type === FormComponentType.TestCase
          ? {
            is_custom: false,
            product_feature_success_criteria: null,
            title: '',
            description: '',
            feature_name: '',
            feature_id: null,
          }
          : {};

    const item: FormMapEntry = {
      formSectionId: uniqueId,
      type,
      change: FormComponentChange.Added,
      data: defaultData,
      children: [],
      parent,
      error: undefined,
      errorType: undefined,
    };

    setFormData((prev) => ({
      ...prev,
      formDataMap: {
        ...(toFront ? { [uniqueId]: item } : {}),
        ...prev.formDataMap,
        ...(!toFront ? { [uniqueId]: item } : {}),
      },
    }));

    if (parent) {
      setItemChild(parent, uniqueId, toFront);
    }

    return uniqueId;
  }, [setItemChild]);

  const addFormItemWithData = useCallback((
    type: FormComponentType,
    data: FormMapEntryData,
    parent: string | undefined = undefined,
    toFront = false
  ) => {
    const uniqueId = `${type}-${uuid()}`;
    const item: FormMapEntry = {
      formSectionId: uniqueId,
      type,
      change: FormComponentChange.Added,
      data,
      children: [],
      parent,
      error: undefined,
      errorType: undefined,
    };

    setFormData((prev) => ({
      ...prev,
      formDataMap: {
        ...(toFront ? { [uniqueId]: item } : {}),
        ...prev.formDataMap,
        ...(!toFront ? { [uniqueId]: item } : {}),
      },
    }));

    if (parent) {
      setItemChild(parent, uniqueId, toFront);
    }

    return uniqueId;
  }, [setItemChild]);

  const setFormItemSaved = useCallback((formId: string, data: Record<string, any>) => {
    setFormData((prev) => ({
      ...prev,
      formDataMap: {
        ...prev.formDataMap,
        [formId]: {
          ...prev.formDataMap[formId],
          change: FormComponentChange.NoChange,
          data,
        },
      },
    }));
  }, []);

  const updateFormItem = useCallback((formId: string, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      formDataMap: {
        ...prev.formDataMap,
        [formId]: {
          ...prev.formDataMap[formId],
          change:
            prev.formDataMap[formId].change === FormComponentChange.Added
              ? FormComponentChange.Added
              : FormComponentChange.Updated,
          data: {
            ...prev.formDataMap[formId]?.data,
            [field]: value,
          },
        },
      },
    }));
  }, []);

  const setFormError = useCallback((formId: string, error: string, errorType: FormErrorType = FormErrorType.Form) => {
    setFormData((prev) => ({
      ...prev,
      formDataMap: {
        ...prev.formDataMap,
        [formId]: {
          ...prev.formDataMap[formId],
          error,
          errorType,
        },
      },
    }));
  }, []);

  const replaceChildren = useCallback((parentId: string, childIds: string[]) => {
    setFormData((prev) => ({
      ...prev,
      formDataMap: {
        ...prev.formDataMap,
        [parentId]: {
          ...prev.formDataMap[parentId],
          children: childIds,
        },
      },
    }));
  }, []);

  const removeFormItem = useCallback((formId: string | string[]) => {
    const formIds = Array.isArray(formId) ? formId : [formId];

    setFormData((prev) => {
      const newMap = { ...prev.formDataMap };

      const parents = formIds.reduce((acc: Record<string, string[]>, id) => {
        const parentId = prev.formDataMap[id]?.parent;
        if (parentId) {
          acc[parentId] = [...(acc[parentId] || []), id];
        }
        return acc;
      }, {});

      for (const parent of Object.keys(parents)) {
        newMap[parent] = {
          ...newMap[parent],
          children: (newMap[parent]?.children ?? []).filter((id) => !parents[parent].includes(id)),
        };
      }

      for (const id of formIds) {
        delete newMap[id];
      }

      return {
        ...prev,
        formDataMap: newMap,
      };
    });
  }, []);

  return (
    <TestCaseFormContext.Provider
      value={{
        opportunityProduct,
        setForm,
        setFormError,
        resetForm,
        getFormIdsForType,
        getFormItem,
        setFormItemSaved,
        addFormItem,
        addFormItemWithData,
        updateFormItem,
        setFormItem,
        removeFormItem,
        replaceChildren,
      }}
    >
      {children}
    </TestCaseFormContext.Provider>
  );
}

export function useTestCaseFormContext() {
  const context = useContext(TestCaseFormContext);
  if (!context) {
    throw new Error('useTestCaseFormContext must be used within a TestCaseFormProvider');
  }
  return context;
}